import React from 'react';
import { Box, Button, ButtonBase, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import Cropper from 'react-cropper';
import type CropperJS from 'cropperjs';
import i18n from '../../i18n';
import { ReactComponent as InfoIcon } from '../../static/images/svg/info.svg';
import { ReactComponent as LightbulbIcon } from '../../static/images/svg/mage_light-bulb-fill.svg';
import { ReactComponent as RotateIcon } from '../../static/images/svg/meteor-icons_rotate.svg';
import { ReactComponent as ResetRotateIcon } from '../../static/images/svg/fa7-solid_rotate.svg';
import {
    getImageBackgroundColor,
    type ImageBackgroundColor,
    type ImageEditMode,
} from '../../utils/image-transform';
import {
    formatImageUploadSuggestion,
    type ImageUploadSpec,
} from '../../utils/image-upload-spec';

/** 图片编辑器的样式类名集合。 */
type ImageCropEditorClasses = Record<string, string>;

/** 图片编辑器入参。 */
interface ImageCropEditorProps {
    /** 页面样式类名。 */
    classes: ImageCropEditorClasses;
    /** 当前编辑源 Object URL；为空时展示选择本地图片入口。 */
    imgSrc: string;
    /** 编辑源序号，用于避免将图片内容拼接为 Cropper key。 */
    cropSourceId: number;
    /** 是否正在读取并降采样图片。 */
    sourceBusy: boolean;
    /** 文件选择控件引用。 */
    fileInputRef: React.RefObject<HTMLInputElement>;
    /** 本地文件选择回调。 */
    onLocalFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    /** 当前裁切实例。 */
    cropper?: CropperJS;
    /** 保存裁切实例。 */
    setCropper: (cropper: CropperJS) => void;
    /** 当前入口的裁切与建议输出规格。 */
    uploadSpec: ImageUploadSpec;
    /** 当前图片编辑模式。 */
    editMode: ImageEditMode;
    /** 更新图片编辑模式。 */
    setEditMode: (value: ImageEditMode) => void;
    /** 适应模式背景色。 */
    backgroundColor: ImageBackgroundColor;
    /** 更新适应模式背景色。 */
    setBackgroundColor: (value: ImageBackgroundColor) => void;
    /** 当前旋转角度。 */
    rotate: number;
    /** 更新旋转角度。 */
    setRotate: (value: number) => void;
    /** 清除当前图片并重新选择。 */
    onReselect: () => void;
    /** 重置裁切与旋转。 */
    onReset: () => void;
    /** 裁切区域变化回调，用于刷新预计上传大小。 */
    onCropChange: () => void;
    /** 原图文件名。 */
    sourceFileName: string;
    /** 原图大小，单位 KB。 */
    sourceFileSizeKb: number;
    /** 当前编辑结果预计上传大小，单位 KB。 */
    estimatedUploadSizeKb: number | null;
    /** 原图尺寸。 */
    sourceImageSize: string;
}

/**
 * 展示本地与网络图片共用的裁切编辑区。
 * @param props - 图片编辑器参数
 * @returns 图片裁切编辑器
 */
export default function ImageCropEditor(
    props: Readonly<ImageCropEditorProps>,
): React.ReactElement {
    const {
        classes,
        imgSrc,
        cropSourceId,
        sourceBusy,
        fileInputRef,
        onLocalFileChange,
        cropper,
        setCropper,
        uploadSpec,
        rotate,
        setRotate,
        onReselect,
        onReset,
        onCropChange,
        sourceFileName,
        sourceFileSizeKb,
        estimatedUploadSizeKb,
        sourceImageSize,
    editMode,
    setEditMode,
    backgroundColor,
    setBackgroundColor,
    } = props;
    const inCrop = Boolean(imgSrc);
    /** 模式菜单锚点。 */
    const [modeAnchorEl, setModeAnchorEl] = React.useState<HTMLElement | null>(null);
    /** 当前模式菜单是否打开。 */
    const modeMenuOpen = Boolean(modeAnchorEl);
    /** 模式按钮文案。 */
    const modeLabel = i18n.t(`image_source_picker_mode_${editMode}`);
    /** 预览区域动态宽高比。 */
    const previewRatio = `${uploadSpec.aspectRatio} / 1`;
    /** 预览区域固定高度，宽度根据当前上传比例计算。 */
    const previewHeight = 400;
    const previewWidth = previewHeight * uploadSpec.aspectRatio;
    /** 适应模式预览背景色。 */
    /** 当前预览背景色，非适应模式不设置背景色。 */
    let previewBackgroundColor: string | undefined;
    if (editMode === 'fit') {
        previewBackgroundColor = getImageBackgroundColor(backgroundColor);
    }
    /** 非裁切模式的图片填充方式。 */
    const imageObjectFit = editMode === 'fill' ? 'cover' : 'contain';
    /** 是否显示 Cropper 裁切内容。 */
    const shouldRenderCropper = Boolean(imgSrc) && editMode === 'crop';
    /** 是否显示填充或适应内容。 */
    const shouldRenderImage = Boolean(imgSrc) && !shouldRenderCropper;
    /** 预计上传大小文案，图片处理中暂显示计算状态。 */
    const estimatedUploadSizeText = estimatedUploadSizeKb == null
        ? i18n.t('image_source_picker_text_info_calculating')
        : `${estimatedUploadSizeKb} KB`;
    /** 当前入口建议的最终图片尺寸。 */
    const suggestedImageSize = formatImageUploadSuggestion(uploadSpec);

    /**
     * 打开图片模式菜单。
     * @param event - 模式按钮点击事件
     */
    const handleModeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setModeAnchorEl(event.currentTarget);
    };

    /**
     * 关闭图片模式菜单。
     */
    const handleModeMenuClose = () => {
        setModeAnchorEl(null);
    };

    /**
     * 选择图片模式并关闭菜单。
     * @param nextMode - 目标图片模式
     */
    const handleModeChange = (nextMode: ImageEditMode) => {
        setEditMode(nextMode);
        handleModeMenuClose();
    };

    return (
        <>
            <input
                ref={fileInputRef}
                accept="image/*"
                className={classes.fileInput}
                type="file"
                onChange={onLocalFileChange}
            />
            <Box className={`${classes.cropLayout} ${classes.responsiveCropLayout}`}>
                <Box
                    className={classes.cropMain}
                    sx={{ width: '100%', maxWidth: 816, alignSelf: 'center' }}
                >
                    <Box className={classes.cropHeader}>
                        <Typography variant="body1" className={classes.cropDescription}>
                            {i18n.t('image_source_picker_text_local_description')}
                        </Typography>
                        <Box className={classes.cropHeaderActions}>
                            <Button
                                className={classes.cropModeButton}
                                variant="outlined"
                                onClick={handleModeMenuOpen}
                                aria-haspopup="menu"
                                aria-expanded={modeMenuOpen ? 'true' : undefined}
                            >
                                {modeLabel}
                            </Button>
                            {imgSrc ? (
                                <Button className={classes.reselectButton} onClick={onReselect}>
                                    {i18n.t('image_source_picker_button_reselect')}
                                </Button>
                            ) : null}
                        </Box>
                        <Menu
                            anchorEl={modeAnchorEl}
                            open={modeMenuOpen}
                            onClose={handleModeMenuClose}
                        >
                            <MenuItem onClick={() => handleModeChange('fill')}>
                                {i18n.t('image_source_picker_mode_fill')}
                            </MenuItem>
                            <MenuItem onClick={() => handleModeChange('fit')}>
                                {i18n.t('image_source_picker_mode_fit')}
                            </MenuItem>
                            <MenuItem onClick={() => handleModeChange('crop')}>
                                {i18n.t('image_source_picker_mode_crop')}
                            </MenuItem>
                        </Menu>
                    </Box>
                    <Box
                        className={classes.cropPreview}
                        sx={{
                            width: previewWidth,
                            height: previewHeight,
                            alignSelf: 'center',
                            aspectRatio: previewRatio,
                            backgroundColor: previewBackgroundColor,
                        }}
                    >
                        {shouldRenderCropper ? (
                            <Cropper
                                key={`${cropSourceId}-${editMode}-${uploadSpec.aspectRatio}`}
                                src={imgSrc}
                                style={{ height: '100%', width: '100%' }}
                                aspectRatio={uploadSpec.aspectRatio}
                                initialAspectRatio={uploadSpec.aspectRatio}
                                guides
                                background={false}
                                viewMode={1}
                                autoCropArea={1}
                                onInitialized={(instance) => {
                                    setCropper(instance);
                                    instance.setAspectRatio(uploadSpec.aspectRatio);
                                    instance.rotateTo(rotate);
                                }}
                                ready={onCropChange}
                                cropend={onCropChange}
                                zoom={onCropChange}
                            />
                        ) : null}
                        {shouldRenderImage ? (
                            <Box
                                component="img"
                                src={imgSrc}
                                alt={i18n.t('image_source_picker_text_local_description')}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: imageObjectFit,
                                    transform: `rotate(${rotate}deg)`,
                                    display: 'block',
                                }}
                            />
                        ) : null}
                        {!imgSrc ? (
                            <ButtonBase
                                focusRipple
                                className={classes.emptyLocal}
                                sx={{ width: '100%', height: '100%', aspectRatio: previewRatio }}
                                onClick={() => fileInputRef.current?.click()}
                                aria-label={i18n.t('image_source_picker_text_select_local')}
                            >
                                <Typography variant="subtitle1" className={classes.emptyLocalText}>
                                    {i18n.t('image_source_picker_text_select_local')}
                                </Typography>
                            </ButtonBase>
                        ) : null}
                        {sourceBusy ? (
                            <Box className={classes.sourceLoading}>
                                <CircularProgress size={32} color="primary" />
                            </Box>
                        ) : null}
                    </Box>
                    <Box className={classes.cropTools}>
                        <Button
                            className={`${classes.cropToolButton} ${classes.cropToolResetButton}`}
                            variant="outlined"
                            sx={{ textTransform: 'none' }}
                            startIcon={<ResetRotateIcon width={24} height={24} />}
                            onClick={onReset}
                        >
                            {i18n.t('image_source_picker_button_reset')}
                        </Button>
                        <Button
                            className={classes.cropToolButton}
                            variant="outlined"
                            sx={{ textTransform: 'none' }}
                            startIcon={
                                <RotateIcon
                                    width={24}
                                    height={24}
                                    style={{ transform: 'scaleX(-1)' }}
                                />
                            }
                            onClick={() => {
                                const next = rotate - 45;
                                setRotate(next);
                                cropper?.rotateTo(next);
                            }}
                        >
                            {i18n.t('image_source_picker_button_rotate_left')}
                        </Button>
                        <Button
                            className={classes.cropToolButton}
                            variant="outlined"
                            sx={{ textTransform: 'none' }}
                            startIcon={<RotateIcon width={24} height={24} />}
                            onClick={() => {
                                const next = rotate + 45;
                                setRotate(next);
                                cropper?.rotateTo(next);
                            }}
                        >
                            {i18n.t('image_source_picker_button_rotate_right')}
                        </Button>
                        {editMode === 'fit' ? (
                            <Box className={classes.backgroundColorButtons}>
                                <ButtonBase
                                    aria-label={i18n.t('image_source_picker_background_black')}
                                    onClick={() => setBackgroundColor('black')}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 2,
                                        backgroundColor: 'var(--fill-neutral-800)',
                                        border:
                                            backgroundColor === 'black'
                                                ? '1px solid var(--stroke-primary-300)'
                                                : '1px solid var(--stroke-neutral-800)',
                                    }}
                                />
                                <ButtonBase
                                    aria-label={i18n.t('image_source_picker_background_white')}
                                    onClick={() => setBackgroundColor('white')}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 2,
                                        backgroundColor: 'var(--fill-neutral-100)',
                                        border:
                                            backgroundColor === 'white'
                                                ? '1px solid var(--stroke-primary-300)'
                                                : '1px solid var(--stroke-neutral-800)',
                                    }}
                                />
                            </Box>
                        ) : null}
                    </Box>
                </Box>
                <Box className={`${classes.cropSide} ${classes.responsiveCropSide}`}>
                    <Box className={classes.tipCard}>
                        <Box className={classes.cardTitleRow}>
                            <InfoIcon width={24} height={24} />
                            <Typography variant="subtitle2" className={classes.cardTitle}>
                                {i18n.t('image_source_picker_text_tips_title')}
                            </Typography>
                        </Box>
                        <Typography variant="body2" className={classes.tipText}>
                            {i18n.t('image_source_picker_text_local_tips_body')}
                        </Typography>
                    </Box>
                    <Box className={classes.infoCard}>
                        <Typography variant="subtitle2" className={classes.cardTitle}>
                            {i18n.t('image_source_picker_text_info_title')}
                        </Typography>
                        <InfoRow
                            classes={classes}
                            label={i18n.t('image_source_picker_text_info_filename')}
                            value={inCrop ? sourceFileName : ''}
                        />
                        <InfoRow
                            classes={classes}
                            label={i18n.t('image_source_picker_text_info_filesize')}
                            value={inCrop ? `${sourceFileSizeKb} KB` : ''}
                        />
                        <InfoRow
                            classes={classes}
                            label={i18n.t('image_source_picker_text_info_estimated_upload_size')}
                            value={inCrop ? estimatedUploadSizeText : ''}
                        />
                        <InfoRow
                            classes={classes}
                            label={i18n.t('image_source_picker_text_info_dimensions')}
                            value={inCrop ? sourceImageSize : ''}
                        />
                    </Box>
                    <Box className={classes.suggestionCard}>
                        <Box className={classes.cardTitleRow}>
                            <LightbulbIcon width={24} height={24} />
                            <Typography variant="subtitle2" className={classes.cardTitle}>
                                {i18n.t('image_source_picker_text_suggest_title')}
                            </Typography>
                        </Box>
                        <Box className={classes.suggestionList}>
                            <Typography variant="body2">
                                {i18n.t('image_source_picker_text_suggest_center')}
                            </Typography>
                            <Typography variant="body2">
                                {i18n.t('image_source_picker_text_suggest_size', {
                                    size: suggestedImageSize,
                                })}
                            </Typography>
                            <Typography variant="body2">
                                {i18n.t('image_source_picker_text_suggest_limit')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
}

/** 图片信息行参数。 */
interface InfoRowProps {
    /** 页面样式类名。 */
    classes: ImageCropEditorClasses;
    /** 信息名称。 */
    label: string;
    /** 信息值。 */
    value: string;
}

/**
 * 展示一行图片信息。
 * @param props - 图片信息行参数
 * @returns 图片信息行
 */
function InfoRow({ classes, label, value }: Readonly<InfoRowProps>): React.ReactElement {
    return (
        <Box className={classes.infoRow}>
            <Typography variant="body2" component="span" className={classes.infoLabel}>
                {label}
            </Typography>
            <Typography variant="body2" component="span" className={classes.infoValue}>
                {value}
            </Typography>
        </Box>
    );
}
